import ApiService from "@/services/api.service";

// ACTIONS
export const LIST_LISTINI_BASE = "listListiniBase";
export const GET_LISTINO_BASE = "getListinoBase";
export const CREATE_LISTINO_BASE = "createListinoBase";
export const UPDATE_LISTINO_BASE = "updateListinoBase";
export const DELETE_LISTINO_BASE = "deleteListinoBase";

// MUTATIONS
export const SET_LIST_LISTINI_BASE = "setListListiniBase";
export const SET_LOADING = "setLoading";
export const SET_ERROR = "setError";
export const SET_ERRORE_CARICAMENTO = "setErroreCaricamento";

// Controller di backend: [Route("api/[controller]")] -> /api/Listino.
// La baseURL di axios è VUE_APP_ROOT_API (default "/api").
// NB: è il listino BASE (voci di tariffa), non il listino per cliente
// (quello è servito da /api/ListinoClienti, modulo store "listini").
const RESOURCE = "Listino";

// Testo usato quando l'errore non contiene un messaggio leggibile.
const MESSAGGIO_ERRORE_GENERICO =
    "Si è verificato un errore, riprova più tardi.";

// Il backend risponde agli errori con HTTP 500 e body {Status, Message}
// (Response<object> di questa codebase). A seconda della configurazione JSON il
// campo arriva come "message" o "Message": si accettano entrambe le forme, più le
// varianti ProblemDetails, e in ogni caso la mutation riceve una stringa.
const toMessage = (err) => {
    if (typeof err === "string") {
        return err.trim() || MESSAGGIO_ERRORE_GENERICO;
    }
    if (!err) {
        return MESSAGGIO_ERRORE_GENERICO;
    }

    const candidati = [
        err.response?.data?.message,
        err.response?.data?.Message,
        err.response?.data?.detail,
        err.response?.data?.title,
        err.response?.data,
        err.data?.message,
        err.data?.Message,
        err.detail,
        err.title,
        err.message,
    ];
    const messaggio = candidati.find(
        (valore) => typeof valore === "string" && valore.trim() !== ""
    );
    if (messaggio) {
        return messaggio.trim();
    }

    try {
        const serializzato = JSON.stringify(err);
        if (serializzato && serializzato !== "{}" && serializzato !== "[]") {
            return serializzato;
        }
    } catch {
        // Oggetto non serializzabile: resta il messaggio generico.
    }

    return MESSAGGIO_ERRORE_GENERICO;
};

const state = {
    list: [],
    loading: false,
    error: {},
    // Errore del caricamento della lista: stringa (messaggio) oppure null.
    // Distingue l'elenco realmente vuoto dal caricamento fallito.
    erroreCaricamento: null,
};

const getters = {
    getList: (state) => state.list,
    getLoading: (state) => state.loading,
    getError: (state) => {
        if (typeof state.error === "string") {
            return state.error;
        }
        if (!state.error || !Object.keys(state.error).length) {
            return null;
        }
        return toMessage(state.error);
    },
    getErroreCaricamento: (state) => state.erroreCaricamento,
};

const actions = {
    // Elenco del listino base. Endpoint: GET /api/Listino
    // Il contratto non prevede parametri di filtro né paginazione: il backend
    // restituisce l'intero array, la tabella filtra e pagina lato client.
    [LIST_LISTINI_BASE](context) {
        context.commit(SET_LOADING, true);
        context.commit(SET_ERRORE_CARICAMENTO, null);

        return new Promise((resolve) => {
            ApiService.get(RESOURCE)
                .then(({ data }) => {
                    context.commit(SET_LIST_LISTINI_BASE, data);
                    context.commit(SET_ERRORE_CARICAMENTO, null);
                    resolve(data);
                })
                .catch((error) => {
                    // Svuota l'elenco committato: senza questo la tabella
                    // mostrerebbe le righe del caricamento precedente mentre
                    // l'alert dice che il caricamento è fallito.
                    context.commit(SET_LIST_LISTINI_BASE, []);
                    const messaggio = toMessage(error);
                    context.commit(SET_ERROR, messaggio);
                    context.commit(SET_ERRORE_CARICAMENTO, messaggio);
                    setTimeout(() => context.commit(SET_ERROR, ""), 5000);
                    resolve([]);
                })
                .finally(() => context.commit(SET_LOADING, false));
        });
    },

    // Lettura di una singola voce. Endpoint: GET /api/Listino/{id}
    // Il contratto prevede 200 con l'oggetto oppure 200 con body null se la voce
    // non esiste: in quel caso il chiamante riceve null (nessuna eccezione).
    [GET_LISTINO_BASE](context, id) {
        return new Promise((resolve) => {
            ApiService.download(RESOURCE, encodeURIComponent(id))
                .then(({ data }) => {
                    resolve(data);
                })
                .catch((error) => {
                    context.commit(SET_ERROR, toMessage(error));
                    setTimeout(() => context.commit(SET_ERROR, ""), 5000);
                    resolve(null);
                });
        });
    },

    // Creazione. Endpoint: POST /api/Listino
    // Body: AddListinoRequest = description, price, billable, invoiceType,
    // codice, codiceListino, idServizio, unitaincluse.
    // Il backend forza ValidFrom = primo giorno del mese corrente e ValidTo = null.
    [CREATE_LISTINO_BASE](context, params) {
        return new Promise((resolve, reject) => {
            ApiService.post(RESOURCE, params)
                .then(({ data }) => {
                    resolve(data);
                })
                .catch((error) => {
                    context.commit(SET_ERROR, toMessage(error));
                    setTimeout(() => context.commit(SET_ERROR, ""), 5000);
                    reject(error);
                });
        });
    },

    // Aggiornamento. Endpoint: PUT /api/Listino/{id}
    // Body: UpdateListinoRequest = come AddListinoRequest, più id e
    // isRetroactiveUpdate, senza codiceListino. Il codice viaggia solo nell'id
    // (rotta) e nel campo id del body: non è modificabile.
    [UPDATE_LISTINO_BASE](context, params) {
        return new Promise((resolve, reject) => {
            ApiService.update(RESOURCE, encodeURIComponent(params.id), params)
                .then(({ data }) => {
                    resolve(data);
                })
                .catch((error) => {
                    context.commit(SET_ERROR, toMessage(error));
                    setTimeout(() => context.commit(SET_ERROR, ""), 5000);
                    reject(error);
                });
        });
    },

    // Eliminazione. Endpoint: DELETE /api/Listino/{id}
    // Fallisce con HTTP 500 se la voce è assegnata a un cliente: il messaggio
    // del backend ("Il listino è associato a un cliente e non è possibile
    // eliminarlo") resta nello store e viene mostrato dalla pagina.
    [DELETE_LISTINO_BASE](context, id) {
        return new Promise((resolve, reject) => {
            ApiService.delete(RESOURCE, encodeURIComponent(id))
                .then(({ data }) => {
                    resolve(data);
                })
                .catch((error) => {
                    context.commit(SET_ERROR, toMessage(error));
                    setTimeout(() => context.commit(SET_ERROR, ""), 5000);
                    reject(error);
                });
        });
    },
};

const mutations = {
    [SET_LIST_LISTINI_BASE](state, list) {
        state.list = Array.isArray(list) ? list : [];
    },
    [SET_LOADING](state, loading) {
        state.loading = loading;
    },
    [SET_ERROR](state, error) {
        state.error = error;
    },
    // Sempre null oppure stringa: distingue l'elenco vuoto dal caricamento fallito.
    [SET_ERRORE_CARICAMENTO](state, valore) {
        state.erroreCaricamento =
            typeof valore === "string" && valore !== "" ? valore : null;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
